/**
 * Pure connection-health state machine (NFR "Connection Health", FR-LIVE-04).
 *
 * Kept transport-free and clock-free on purpose: every input is a method call
 * and every clock read is a parameter, so the whole traffic-light can be driven
 * deterministically from a test without timers or sockets.
 *
 * | State  | Bars | Condition                                                     |
 * |--------|------|---------------------------------------------------------------|
 * | GREEN  | 4    | connected AND rolling p95 RTT < 250 ms AND no missed heartbeat |
 * | YELLOW | 2    | connected AND (p95 250–1000 ms OR 1 missed heartbeat in 15 s)  |
 * | RED    | 0    | disconnected OR ≥2 consecutive misses OR p95 > 1000 ms         |
 *
 * Debounce: a move **into RED is immediate** — the operator must know at once
 * that their plays are being queued. Every other move needs
 * {@link CONNECTION_HEALTH.debounceSamples} consecutive evaluations in the new
 * band, so one slow ping does not flicker the indicator mid-drive.
 */

export type ConnectionHealthState = 'GREEN' | 'YELLOW' | 'RED';

/** Thresholds are the spec's, in one place so the UI and telemetry agree. */
export const CONNECTION_HEALTH = {
  /** Heartbeat cadence over WebSocket. */
  pingIntervalMs: 3000,
  /** Heartbeat cadence over the SSE fallback transport. */
  ssePingIntervalMs: 5000,
  /** Rolling window the p95 is computed over. */
  rollingWindowMs: 15_000,
  /** Hard cap on retained samples (≈60 s at 3 s cadence). */
  maxSamples: 20,
  greenMaxRttMs: 250,
  yellowMaxRttMs: 1000,
  /** Consecutive evaluations required before a non-RED transition shows. */
  debounceSamples: 2,
  /** Consecutive misses that force RED. */
  redMissedPings: 2,
  /** Disconnected for this long → advise the SSE fallback. */
  sseFallbackAfterMs: 10_000,
  /** Telemetry sample cadence, and the relaxed cadence when stable GREEN. */
  sampleIntervalMs: 30_000,
  stableSampleIntervalMs: 60_000,
  stableAfterMs: 300_000,
} as const;

/** Bars drawn by the indicator for each state. */
export const HEALTH_BARS: Record<ConnectionHealthState, number> = { GREEN: 4, YELLOW: 2, RED: 0 };

export interface ConnectionHealthSnapshot {
  state: ConnectionHealthState;
  /** Rolling p95 RTT in ms; `0` when nothing has been measured yet. */
  latencyMs: number;
  /** Band the raw measurements are in, before debounce. */
  band: ConnectionHealthState;
  isDebouncePending: boolean;
  isTransportConnected: boolean;
  missedPings: number;
}

interface Rtt {
  at: number;
  rttMs: number;
}

/** p95 of the samples inside the rolling window (nearest-rank). */
function percentile95(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((left, right) => left - right);
  const rank = Math.ceil(0.95 * sorted.length) - 1;
  return sorted[Math.min(rank, sorted.length - 1)] ?? 0;
}

export class ConnectionHealthMachine {
  private samples: Rtt[] = [];
  private missedPings = 0;
  private lastMissAt: number | null = null;
  private isConnected = false;
  private displayed: ConnectionHealthState = 'RED';
  private pendingBand: ConnectionHealthState | null = null;
  private pendingCount = 0;

  /** Transport open/closed. A close is reported as RED on the next evaluate. */
  setConnected(isConnected: boolean): void {
    if (isConnected === this.isConnected) return;
    this.isConnected = isConnected;
    if (!isConnected) {
      this.samples = [];
      this.missedPings = 0;
      this.lastMissAt = null;
    }
  }

  /** A pong came back in `rttMs`. */
  recordPong(rttMs: number, at: number): void {
    this.missedPings = 0;
    this.samples = [...this.samples, { at, rttMs }].slice(-CONNECTION_HEALTH.maxSamples);
  }

  /** No pong arrived before the next heartbeat was due. */
  recordMissedPing(at: number): void {
    this.missedPings += 1;
    this.lastMissAt = at;
  }

  /** Raw band from the measurements, ignoring debounce. */
  band(at: number): ConnectionHealthState {
    if (!this.isConnected) return 'RED';
    if (this.missedPings >= CONNECTION_HEALTH.redMissedPings) return 'RED';
    const latency = this.latencyMs(at);
    if (latency > CONNECTION_HEALTH.yellowMaxRttMs) return 'RED';
    const hasRecentMiss =
      this.lastMissAt !== null && at - this.lastMissAt <= CONNECTION_HEALTH.rollingWindowMs;
    if (hasRecentMiss) return 'YELLOW';
    // Connected but nothing measured yet is "unknown", which shows as YELLOW
    // rather than claiming a health we have not verified.
    if (this.samples.length === 0) return 'YELLOW';
    if (latency >= CONNECTION_HEALTH.greenMaxRttMs) return 'YELLOW';
    return 'GREEN';
  }

  latencyMs(at: number): number {
    const cutoff = at - CONNECTION_HEALTH.rollingWindowMs;
    const inWindow = this.samples.filter((sample) => sample.at >= cutoff);
    return percentile95(inWindow.map((sample) => sample.rttMs));
  }

  /**
   * Fold one evaluation tick into the displayed state and return the snapshot.
   * Call this on every heartbeat (3 s) — the debounce counts these calls.
   */
  evaluate(at: number): ConnectionHealthSnapshot {
    const band = this.band(at);
    if (band === this.displayed) {
      this.pendingBand = null;
      this.pendingCount = 0;
    } else if (band === 'RED') {
      this.displayed = 'RED';
      this.pendingBand = null;
      this.pendingCount = 0;
    } else {
      this.pendingCount = this.pendingBand === band ? this.pendingCount + 1 : 1;
      this.pendingBand = band;
      if (this.pendingCount >= CONNECTION_HEALTH.debounceSamples) {
        this.displayed = band;
        this.pendingBand = null;
        this.pendingCount = 0;
      }
    }
    return this.snapshot(at, band);
  }

  snapshot(at: number, band: ConnectionHealthState = this.band(at)): ConnectionHealthSnapshot {
    return {
      state: this.displayed,
      latencyMs: this.latencyMs(at),
      band,
      isDebouncePending: this.pendingBand !== null,
      isTransportConnected: this.isConnected,
      missedPings: this.missedPings,
    };
  }
}
